import React, { useEffect, useState, useCallback } from 'react';
import { preiseAbrufen } from '../api';
import './PricingModal.css';

function planMeta(p, monatlichBetrag) {
  const euro = p.betrag / 100;
  const isYear    = p.interval === 'year';
  const isQuartal = p.interval === 'month' && p.interval_count === 3;

  if (isYear) {
    const proMonat = (euro / 12).toFixed(2).replace('.', ',');
    const ersparnis = monatlichBetrag != null ? Math.floor(monatlichBetrag * 12 - euro) : null;
    return {
      tag: 'Bester Preis', star: false,
      name: 'Pro Jahr',
      text: 'Für langfristige Jobsuche und Wechselbereitschaft',
      periode: '/ Jahr',
      features: [
        'Alles aus Pro',
        'Bewerbungen jederzeit vorbereiten',
        `Ca. ${proMonat} € pro Monat`,
        ersparnis != null ? `Spare über ${ersparnis} € jährlich` : null,
      ].filter(Boolean),
    };
  }
  if (isQuartal) {
    const proMonat = (euro / 3).toFixed(2).replace('.', ',');
    const pct = monatlichBetrag != null ? Math.round((1 - euro / 3 / monatlichBetrag) * 100) : null;
    return {
      tag: 'Beliebt', star: true,
      name: 'Pro Quartal',
      text: 'Für mehrere Bewerbungen mit Ruhe',
      periode: '/ Quartal',
      features: [
        'Alles aus Pro',
        'Ideal für längere Bewerbungsphasen',
        `Ca. ${proMonat} € pro Monat`,
        pct != null ? `${pct} % günstiger als monatlich` : null,
      ].filter(Boolean),
    };
  }
  return {
    tag: 'Flexibel', star: false,
    name: 'Pro Monatlich',
    text: 'Für aktive Bewerbungsphasen',
    periode: '/ Monat',
    features: [
      'Bewerbungen passend zur Stelle vorbereiten',
      'Lebenslauf und Anschreiben bearbeiten',
      'Job-Fit Analyse',
    ],
  };
}

function SkeletonGrid() {
  return (
    <div className="pricing-skeleton-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      {[0, 1, 2].map(i => <div key={i} className="pricing-skeleton-card" />)}
    </div>
  );
}

export default function PricingModal({ offen, onSchliessen, onKaufen, kauftGrade }) {
  const [preise, setPreise]           = useState([]);
  const [laden, setLaden]             = useState(true);
  const [fehler, setFehler]           = useState(false);
  const [gewaehlterPreis, setGewaehlt] = useState(null);
  const [agbOk, setAgbOk]             = useState(false);
  const [widerrufsOk, setWiderrufsOk] = useState(false);

  const laden_ = useCallback(() => {
    setLaden(true);
    setFehler(false);
    preiseAbrufen()
      .then(r => {
        const liste = r.data.filter(p => p.paket_typ !== 'free');
        setPreise(liste);
        const bestseller = liste.find(p => p.beliebt) || liste[1] || liste[0];
        if (bestseller) setGewaehlt(bestseller.id);
      })
      .catch(() => setFehler(true))
      .finally(() => setLaden(false));
  }, []);

  useEffect(() => { if (offen) laden_(); }, [offen, laden_]);

  useEffect(() => {
    if (!offen) return;
    const handler = e => { if (e.key === 'Escape') onSchliessen(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [offen, onSchliessen]);

  if (!offen) return null;

  const monatlich = preise.find(p => p.interval === 'month' && p.interval_count === 1);
  const monatlichBetrag = monatlich ? monatlich.betrag / 100 : null;

  const handleKaufen = () => {
    if (!gewaehlterPreis) return;
    onKaufen(gewaehlterPreis, new Date().toISOString());
  };

  return (
    <div className="pricing-overlay" onClick={e => { if (e.target === e.currentTarget) onSchliessen(); }}>
      <div className="pricing-modal" role="dialog" aria-modal="true" aria-label="Paket auswählen">

        <div className="pricing-header">
          <div className="pricing-header-copy">
            <span className="pricing-eyebrow">Abo wählen</span>
            <h2 className="pricing-title">Wähle dein Abonnement</h2>
          </div>
          <button className="pricing-close" onClick={onSchliessen} aria-label="Schließen">×</button>
        </div>

        {laden && <SkeletonGrid />}

        {!laden && fehler && (
          <div className="pricing-fehler">
            <p className="pricing-fehler-text">Pakete konnten nicht geladen werden.</p>
            <button className="pricing-retry-btn" onClick={laden_}>Erneut versuchen</button>
          </div>
        )}

        {!laden && !fehler && (
          <div className="pricing-grid" style={{ gridTemplateColumns: `repeat(${preise.length}, 1fr)` }}>
            {preise.map(p => {
              const meta = planMeta(p, monatlichBetrag);
              const selected = gewaehlterPreis === p.id;
              const betragStr = (p.betrag / 100).toFixed(2).replace('.', ',');
              return (
                <div
                  key={p.id}
                  className={[
                    'pricing-card',
                    meta.star ? 'pricing-card--beliebt' : '',
                    selected ? 'pricing-card--selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setGewaehlt(p.id)}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') setGewaehlt(p.id); }}
                >
                  <div className="pricing-trial">1 Woche kostenlos testen</div>

                  <span className={`pricing-badge${meta.star ? '' : ' pricing-badge--default'}`}>
                    {meta.star && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 3 }}>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    )}
                    {meta.tag}
                  </span>

                  <div className="pricing-plan-name">{meta.name}</div>
                  <div className="pricing-plan-text">{meta.text}</div>

                  <div className="pricing-preis-row">
                    <span className="pricing-waehrung">€</span>
                    <span className="pricing-betrag">{betragStr}</span>
                    <span className="pricing-periode">{meta.periode}</span>
                  </div>

                  <div className="pricing-features">
                    {meta.features.map((f, i) => (
                      <React.Fragment key={i}>
                        <div className="pricing-feature-item">{f}</div>
                        {i < meta.features.length - 1 && <div className="pricing-feature-divider" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!laden && !fehler && preise.length > 0 && (
          <>
            <label className="pricing-agb-zeile">
              <input
                type="checkbox"
                checked={agbOk}
                onChange={e => setAgbOk(e.target.checked)}
              />
              <span>
                Ich habe die{' '}
                <a href="https://cvflow.de/agb" target="_blank" rel="noopener noreferrer">AGB</a>
                {' '}und die{' '}
                <a href="https://cvflow.de/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>
                {' '}gelesen und stimme ihnen zu.
              </span>
            </label>
            <label className="pricing-agb-zeile">
              <input
                type="checkbox"
                checked={widerrufsOk}
                onChange={e => setWiderrufsOk(e.target.checked)}
              />
              <span>
                Ich bestätige, dass mit der Ausführung des digitalen Produkts sofort begonnen wird und ich damit mein Widerrufsrecht verliere (§ 356 Abs. 5 BGB).
              </span>
            </label>
            <button
              className="pricing-cta pricing-cta--beliebt pricing-cta--global"
              disabled={!gewaehlterPreis || !agbOk || !widerrufsOk || kauftGrade}
              onClick={handleKaufen}
            >
              {kauftGrade ? 'Weiterleitung…' : 'Jetzt abonnieren →'}
            </button>
          </>
        )}

        <div className="pricing-footer">
          <svg className="pricing-footer-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Sichere Zahlung via Stripe · SSL-verschlüsselt ·{' '}
          <a href="https://cvflow.de/agb" target="_blank" rel="noopener noreferrer">AGB</a>
          {' '}·{' '}
          <a href="https://cvflow.de/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutz</a>
        </div>

      </div>
    </div>
  );
}
